import React, { useEffect, useRef } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { MapContext } from '../../context/MapContext'
import { useCity } from '../../context/CityContext'

const markerIcon = L.divIcon({
    className: '',
    html: '<div class="map-marker"></div>',
    iconSize: [16, 16],
    iconAnchor: [8, 8],
})

const driverMarkerIcon = L.divIcon({
    className: '',
    html: '<div class="driver-marker"></div>',
    iconSize: [22, 22],
    iconAnchor: [11, 11],
})

const currentThemeColor = () =>
    getComputedStyle(document.documentElement)
        .getPropertyValue('--theme-color')
        .trim() || '#48c0f3'

export const MapLayer = ({ interactive = true }: { interactive?: boolean }) => {
    const { points, route, addPointByCoords, driverLocation } =
        React.useContext(MapContext)
    const { selectedCity } = useCity()

    const containerRef = useRef<HTMLDivElement>(null)
    const mapRef = useRef<L.Map | null>(null)
    const markersRef = useRef<L.LayerGroup | null>(null)
    const routeRef = useRef<L.LayerGroup | null>(null)
    const driverRef = useRef<L.LayerGroup | null>(null)
    const interactiveRef = useRef(interactive)

    useEffect(() => {
        interactiveRef.current = interactive
    }, [interactive])

    useEffect(() => {
        if (!containerRef.current || mapRef.current) return

        const map = L.map(containerRef.current, {
            zoomControl: false
        }).setView([55.02549271058871, 82.90275126829616], 14)

        L.control.zoom({ position: 'bottomright' }).addTo(map)

        L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: '&copy; OpenStreetMap contributors'
        }).addTo(map)

        map.on('click', (e: L.LeafletMouseEvent) => {
            if (!interactiveRef.current) return
            addPointByCoords([e.latlng.lng, e.latlng.lat])
        })

        mapRef.current = map
        markersRef.current = L.layerGroup().addTo(map)
        routeRef.current = L.layerGroup().addTo(map)
        driverRef.current = L.layerGroup().addTo(map)

        return () => {
            map.remove()
            mapRef.current = null
            markersRef.current = null
            routeRef.current = null
            driverRef.current = null
        }
    }, [])

    // Переключаем карту на выбранный город.
    useEffect(() => {
        const map = mapRef.current
        if (!map || !selectedCity) return
        map.setView([selectedCity.latitude, selectedCity.longitude], 12)
    }, [selectedCity])

    useEffect(() => {
        const layer = markersRef.current
        if (!layer) return
        layer.clearLayers()
        points.forEach((point) => {
            L.marker([point.latitude ?? 0, point.longitude ?? 0], {
                icon: markerIcon
            }).addTo(layer)
        })
    }, [points])

    useEffect(() => {
        const layer = routeRef.current
        if (!layer) return
        layer.clearLayers()
        if (route) {
            L.geoJSON(route, {
                style: { color: currentThemeColor(), weight: 4 }
            }).addTo(layer)
        }
    }, [route])

    useEffect(() => {
        const layer = driverRef.current
        if (!layer) return
        layer.clearLayers()
        if (driverLocation) {
            L.marker(
                [driverLocation.latitude, driverLocation.longitude],
                { icon: driverMarkerIcon }
            ).addTo(layer)
        }
    }, [driverLocation])

    return <div ref={containerRef} className='div-map' style={{ width: '100%', height: '100%' }} />
}
